import { Archive, Factory, GitBranch, GitPullRequest } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import type { IssueModalState } from "@/lib/issue-modal";
import { cn } from "@/lib/utils";
import type { IssuesMeta, IssueView } from "../../types";
import { TimeAgo } from "../time-ago";
import { Checkbox } from "../ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  LabelChip,
  PRIORITY_LABELS,
  PriorityIcon,
  SlaPill,
  StatusDisc,
  TeamChip,
  TypeIcon,
  UserChip,
  WaitingPill,
} from "./meta";

export function IssueRow({
  issue,
  meta,
  selected = false,
  onToggleSelect,
  showWaiting = true,
}: {
  issue: IssueView;
  meta: IssuesMeta | undefined;
  /** False when the list is already grouped by what the issue waits on. */
  showWaiting?: boolean;
  /** Whether this row is part of the current multi-selection. */
  selected?: boolean;
  /**
   * Present only while the list is in select mode: shows the checkbox and makes a
   * click anywhere on the row toggle it instead of opening the issue. `range` is
   * the shift key, for span selection.
   */
  onToggleSelect?: (issue: IssueView, range: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  // Phones get the issue page. Wider screens keep the list underneath a modal.
  const modalState: IssueModalState | undefined = isMobile ? undefined : { backgroundLocation: location };
  const status = meta?.statuses.find((s) => s.key === issue.statusKey);
  const type = meta?.types.find((t) => t.key === issue.typeKey);
  // One repo is the whole workspace; the chip only earns its place once there is a choice.
  const repo = meta && meta.repos.length > 1 ? meta.repos.find((r) => r.key === issue.repoKey) : undefined;
  const selectMode = Boolean(onToggleSelect);
  const done = status?.category === "completed";
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the title inside is the keyboard-accessible link
    <li
      className={cn(
        "group/row flex min-h-9 cursor-pointer items-center gap-2.5 px-gutter py-1.5 transition-colors duration-(--motion-dur) hover:bg-muted/50",
        // Long infinite-scrolled lists: the browser skips layout and paint for rows off screen.
        "[contain-intrinsic-size:auto_36px] [content-visibility:auto]",
        selected && "bg-primary/5 hover:bg-primary/10",
        selectMode && "select-none",
        issue.archivedAt && "text-muted-foreground",
      )}
      data-selected={selected || undefined}
      // Shift-click would otherwise highlight text across the rows in the span.
      onMouseDown={(e) => selectMode && e.shiftKey && e.preventDefault()}
      onClick={(e) => {
        if (onToggleSelect) onToggleSelect(issue, e.shiftKey);
        else navigate(`/issues/${issue.identifier}`, modalState ? { state: modalState } : undefined);
      }}
    >
      {onToggleSelect && (
        <Checkbox
          checked={selected}
          aria-label={`Select ${issue.identifier}`}
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(issue, e.shiftKey);
          }}
        />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="shrink-0">
            <PriorityIcon priority={issue.priority} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{PRIORITY_LABELS[issue.priority]}</TooltipContent>
      </Tooltip>
      <span className="w-17.5 shrink-0 font-mono text-[11px] whitespace-nowrap text-muted-foreground">
        {issue.identifier}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="shrink-0">
            <StatusDisc category={status?.category} color={status?.color} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{status?.name ?? issue.statusKey}</TooltipContent>
      </Tooltip>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Link
          to={`/issues/${issue.identifier}`}
          state={modalState}
          className={cn(
            "line-clamp-2 text-sm break-words decoration-border underline-offset-[3px] group-hover/row:underline md:line-clamp-none md:truncate",
            done && "text-muted-foreground line-through decoration-muted-foreground/50",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {issue.title}
        </Link>
        {issue.subIssues.total > 0 && (
          <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground tabular-nums">
            {issue.subIssues.done}/{issue.subIssues.total}
          </span>
        )}
        {showWaiting && <WaitingPill issue={issue} />}
        {issue.archivedAt && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Archive className="size-3.5 shrink-0" />
            </TooltipTrigger>
            <TooltipContent>Archived</TooltipContent>
          </Tooltip>
        )}
        {issue.taskId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Factory className="size-3.5 shrink-0 text-primary/70" />
            </TooltipTrigger>
            <TooltipContent>
              {issue.currentAgentId ? `In factory · ${issue.currentAgentId}` : "In factory"}
            </TooltipContent>
          </Tooltip>
        )}
        {issue.prUrl && (
          <a
            href={issue.prUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Pull request"
          >
            <GitPullRequest className="size-3.5 shrink-0 text-muted-foreground hover:text-foreground" />
          </a>
        )}
      </div>
      <div className="hidden items-center gap-1.5 md:flex">
        {issue.labels.slice(0, 3).map((label) => (
          <LabelChip key={label.id} name={label.name} color={label.color} className="hidden lg:inline-flex" />
        ))}
        {type && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <TypeIcon typeKey={type.key} color={type.color} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{type.name}</TooltipContent>
          </Tooltip>
        )}
        {issue.teams.map((team) => (
          <TeamChip key={team.id} name={team.key} color={team.color} className="hidden xl:inline-flex" />
        ))}
        {repo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="hidden items-center gap-1 rounded-sm border px-1.5 font-mono text-[11px] text-muted-foreground xl:inline-flex">
                <GitBranch className="size-3" />
                {repo.key}
              </span>
            </TooltipTrigger>
            <TooltipContent>{repo.name}</TooltipContent>
          </Tooltip>
        )}
        <SlaPill issue={issue} className="hidden sm:inline-flex" />
        {issue.storyPoints != null && (
          <span className="hidden rounded border px-1 text-[11px] text-muted-foreground tabular-nums sm:inline">
            {issue.storyPoints}
          </span>
        )}
      </div>
      <UserChip user={issue.reviewer} />
      <span className="hidden w-14 shrink-0 text-right text-xs text-muted-foreground tabular-nums lg:inline">
        <TimeAgo at={issue.createdAt} />
      </span>
    </li>
  );
}
